/* ============================================================================
   PlanForm — criar / editar plano (preços, cor, recomendado, características).
   ============================================================================ */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../components/AdminLayout';
import { Field, TextInput, Switch, ColorPicker } from '../components/Field';
import { toast } from '../components/Toast';
import { IconBack, IconPlus, IconTrash, IconGrip, IconCheck, IconX } from '../components/icons';
import './PlanForm.css';

const EMPTY = { name: '', monthlyPrice: 0, quarterlyPrice: 0, isRecommended: false, color: '#d17d34', features: [{ text: '', included: true }] };

export default function PlanForm() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const isEdit = !!planId;

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => { if (planId) loadPlan(); }, [planId]); // eslint-disable-line

  const loadPlan = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/plans', true);
      if (res.success) {
        const plan = (res.data || []).find((p) => p.id === planId);
        if (plan) set({
          name: plan.name || '', monthlyPrice: plan.monthlyPrice || 0, quarterlyPrice: plan.quarterlyPrice || 0,
          isRecommended: plan.isRecommended || false, color: plan.color || '#d17d34',
          features: plan.features?.length ? plan.features.map((f) => typeof f === 'string' ? { text: f, included: true } : f) : [{ text: '', included: true }],
        });
      }
    } catch { toast.error('Erro ao carregar o plano.'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = { ...form, features: form.features.filter((f) => f.text.trim()) };
      if (isEdit) await api.put(`/admin/plans/${planId}`, payload, true);
      else await api.post('/admin/plans', payload, true);
      toast.success(isEdit ? 'Plano atualizado.' : 'Plano criado.');
      navigate('/treinamentos/manager/plans');
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar o plano.');
      setSaving(false);
    }
  };

  /* ── Características ── */
  const setFeature = (i, patch) => set({ features: form.features.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const addFeature = () => set({ features: [...form.features, { text: '', included: true }] });
  const removeFeature = (i) => {
    const rest = form.features.filter((_, idx) => idx !== i);
    set({ features: rest.length ? rest : [{ text: '', included: true }] });
  };

  const onDrop = (i) => {
    if (dragIndex === null || dragIndex === i) return;
    const features = [...form.features];
    const [moved] = features.splice(dragIndex, 1);
    features.splice(i, 0, moved);
    set({ features });
    setDragIndex(null);
  };

  if (loading && !form.name && isEdit) {
    return <AdminLayout><div className="mg-loading"><div className="mg-spinner" /></div></AdminLayout>;
  }

  return (
    <AdminLayout narrow>
      <button className="cf-breadcrumb" onClick={() => navigate('/treinamentos/manager/plans')}><IconBack size={15} /> Planos</button>
      <h1 className="mg-page-title">{isEdit ? 'Editar plano' : 'Novo plano'}</h1>
      <p className="mg-page-sub" style={{ marginBottom: 28 }}>
        {isEdit ? 'Atualize as informações do plano.' : 'Preencha os dados para criar um novo plano.'}
      </p>

      <form onSubmit={handleSubmit} className="pf-form">
        <section className="mg-card">
          <h2 className="cf-card-title"><span className="cf-num">01</span> Informações</h2>
          <Field label="Nome do plano" required>
            <TextInput value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ex: Bronze, Prata, Ouro" required />
          </Field>
          <div className="mg-field-row">
            <Field label="Preço mensal (R$)" required>
              <TextInput type="number" min="0" step="0.01" value={form.monthlyPrice} onChange={(e) => set({ monthlyPrice: parseFloat(e.target.value) || 0 })} required />
            </Field>
            <Field label="Preço trimestral (R$)" required>
              <TextInput type="number" min="0" step="0.01" value={form.quarterlyPrice} onChange={(e) => set({ quarterlyPrice: parseFloat(e.target.value) || 0 })} required />
            </Field>
          </div>
          <Field label="Plano recomendado" hint="Destaca o plano como melhor custo-benefício">
            <Switch id="rec" checked={form.isRecommended} onChange={(v) => set({ isRecommended: v })} label="Marcar como recomendado" />
          </Field>
        </section>

        <section className="mg-card">
          <h2 className="cf-card-title"><span className="cf-num">02</span> Cor do plano</h2>
          <p className="mg-hint" style={{ marginTop: -12, marginBottom: 18 }}>Usada na badge do plano nos cursos e no catálogo.</p>
          <div className="pf-color-row">
            <ColorPicker color={form.color} onChange={(color) => set({ color })} />
            <div className="pf-color-preview">
              <div className="pf-color-swatch" style={{ background: form.color }} />
              <span className="mg-badge" style={{ background: `${form.color}22`, color: form.color, borderColor: `${form.color}55` }}>
                {form.name || 'Preview'}
              </span>
            </div>
          </div>
        </section>

        <section className="mg-card">
          <h2 className="cf-card-title"><span className="cf-num">03</span> Características</h2>
          <p className="mg-hint" style={{ marginTop: -12, marginBottom: 18 }}>Arraste para reordenar. Desmarque o ✓ para indicar que o plano NÃO inclui o item.</p>
          <div className="pf-features">
            {form.features.map((feature, i) => (
              <div
                key={i}
                className={`pf-feature${dragIndex === i ? ' is-dragging' : ''}`}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                onDragEnd={() => setDragIndex(null)}
              >
                <span className="pf-feature-grip"><IconGrip size={16} /></span>
                <button
                  type="button"
                  className={`pf-feature-toggle${feature.included ? ' is-on' : ''}`}
                  onClick={() => setFeature(i, { included: !feature.included })}
                  title={feature.included ? 'Incluído' : 'Não incluído'}
                >
                  {feature.included ? <IconCheck size={13} /> : <IconX size={13} />}
                </button>
                <input
                  className="mg-input"
                  style={{ opacity: feature.included ? 1 : 0.55 }}
                  value={feature.text}
                  onChange={(e) => setFeature(i, { text: e.target.value })}
                  placeholder={`Característica ${i + 1}`}
                />
                <button type="button" className="mg-icon-btn is-danger" onClick={() => removeFeature(i)}><IconTrash size={15} /></button>
              </div>
            ))}
          </div>
          <button type="button" className="mg-btn mg-btn-ghost mg-btn-sm" style={{ marginTop: 12 }} onClick={addFeature}><IconPlus size={15} /> Adicionar característica</button>
        </section>

        <div className="cf-actions">
          <button type="button" className="mg-btn mg-btn-ghost" onClick={() => navigate('/treinamentos/manager/plans')}>Cancelar</button>
          <button type="submit" className="mg-btn mg-btn-primary" disabled={saving}>
            {saving ? <><span className="mg-spinner mg-spinner-sm" /> Salvando…</> : (isEdit ? 'Salvar alterações' : 'Criar plano')}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
